import { Field, Form, Formik, useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import { Button, Modal, ModalBody, ModalHeader } from 'reactstrap';
import * as Yup from 'yup'; // Import Yup for validation
import FormikInputField from 'components/forms/formikFields/FormikInputField';

const JobExperienceEditModal = ({ modal, setModal }) => {
    const { values, setFieldValue } = useFormikContext();
    const [experienceLevel, setExperienceLevel] = useState(values?.experienceLevel);
    const [showInputs, setShowInputs] = useState(false);

    const handleExperienceLevelChange = (event) => {
        const selectedLevel = event.target.value;
        setFieldValue('experienceLevel', selectedLevel);

        // Set default values based on selectedLevel
        if (selectedLevel === 'beginner') {
            setShowInputs(false);
            setFieldValue('minExperience', 0);
            setFieldValue('maxExperience', 5);
        } else if (selectedLevel === 'mid') {
            setShowInputs(false);
            setFieldValue('minExperience', 6);
            setFieldValue('maxExperience', 10);
        } else if (selectedLevel === 'expert') {
            setShowInputs(false);
            setFieldValue('minExperience', 11);
            setFieldValue('maxExperience', 30);
        } else {
            setShowInputs(true);
        }
    };

    useEffect(() => {
        if (values.maxExperience <= 5) {
            setExperienceLevel('beginner');
        } else if (values.maxExperience <= 10) {
            setExperienceLevel('mid');
        } else if (values.maxExperience > 10) {
            setExperienceLevel('expert');
        } else {
            setExperienceLevel(null);
        }
    }, [values.minExperience, values.maxExperience]);

    const handleClose = () => {
        setModal(false);
    };

    return (
        <Formik
            initialValues={{
                experienceLevel: experienceLevel || '',
                minExperience: values.minExperience || 0,
                maxExperience: values.maxExperience || 0,
            }}
            validationSchema={Yup.object({
                minExperience: Yup.number()
                    .min(0, 'Minimum experience must be at least 0 years')
                    .required('Minimum experience is required'),
                maxExperience: Yup.number()
                    .min(Yup.ref('minExperience'), 'Maximum experience must be greater than or equal to minimum experience')
                    .required('Maximum experience is required'),
            })}
            onSubmit={(values) => {
                if (showInputs) {
                    setFieldValue('minExperience', String(values.minExperience).replace(/^0+/, '') || '0');
                    setFieldValue('maxExperience', String(values.maxExperience).replace(/^0+/, '') || '0');
                }
                setModal(false);
            }}
        >
            {({ handleSubmit, errors, touched }) => (
                <Form>
                    <Modal isOpen={modal} toggle={handleClose} className="nex-model-v2 modal-dialog-scrollable modal-dialog-centered">
                        <ModalHeader toggle={handleClose}>Edit Job Experience</ModalHeader>
                        <ModalBody>
                            <div className="nex-job-form-right-sec">
                                <h2 className="nex-text-xl-small mb-4">How many years of experience are you looking for?</h2>
                                <div className="form-group nex-radio-content-group">
                                    <div className="cus-radio-w">
                                        <input
                                            type="radio"
                                            name="experienceLevel"
                                            value="beginner"
                                            onChange={handleExperienceLevelChange}
                                            checked={experienceLevel === 'beginner'}
                                        />
                                    </div>
                                    <div className="info">
                                        <h3 className="card-title">Beginner</h3>
                                        <p>Someone who has an experience of 0 to 5 years</p>
                                    </div>
                                </div>
                                <div className="form-group nex-radio-content-group">
                                    <div className="cus-radio-w">
                                        <input
                                            type="radio"
                                            name="experienceLevel"
                                            value="mid"
                                            onChange={handleExperienceLevelChange}
                                            checked={experienceLevel === 'mid'}
                                        />
                                    </div>
                                    <div className="info">
                                        <h3 className="card-title">Mid-Level</h3>
                                        <p>Someone who has an experience of 6 to 10 years</p>
                                    </div>
                                </div>
                                <div className="form-group nex-radio-content-group">
                                    <div className="cus-radio-w">
                                        <input
                                            type="radio"
                                            name="experienceLevel"
                                            value="expert"
                                            onChange={handleExperienceLevelChange}
                                            checked={experienceLevel === 'expert'}
                                        />
                                    </div>
                                    <div className="info">
                                        <h3 className="card-title">Expert</h3>
                                        <p>Someone who has an experience of more than 10 years</p>
                                    </div>
                                </div>
                                <div className="form-group nex-radio-content-group">
                                    <div className="cus-radio-w">
                                        <input
                                            type="radio"
                                            name="experienceLevel"
                                            value=""
                                            onChange={handleExperienceLevelChange}
                                            checked={showInputs}
                                        />
                                    </div>
                                    <div className="info">
                                        <h3 className="card-title">Add your own</h3>
                                        <p>I would like to set the experience</p>
                                    </div>
                                </div>
                                {showInputs && (
                                    <div className="nex-flex nex-gap-30 nex-job-experience">
                                        <div className="form-group nex-flex nex-gap-16">
                                            <Field
                                                className="nex-max-w-100 nex-gap-16"
                                                id="minExperience"
                                                name="minExperience"
                                                autoComplete="minExperience"
                                                type="number"
                                                min="0"
                                                component={FormikInputField}
                                                placeholder="Min"
                                            />
                                            <div className="label">Years</div>
                                        </div>
                                        <div className="form-group nex-flex nex-gap-16">
                                            <Field
                                                className="nex-max-w-100"
                                                id="maxExperience"
                                                name="maxExperience"
                                                autoComplete="maxExperience"
                                                type="number"
                                                min="0"
                                                component={FormikInputField}
                                                placeholder="Max"
                                            />
                                            <div className="label">Years</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="form-group nex-action-flex">
                                <Button
                                    id="nex-pa-about-submit-btn"
                                    className="nex-btn-primary mr-2"
                                    color="primary"
                                    onClick={handleSubmit}
                                >
                                    Save
                                </Button>
                                <Button
                                    id="nex-pa-about-cancel-btn"
                                    onClick={handleClose}
                                    className="nex-btn-primary-outline"
                                    color="primary"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </ModalBody>
                    </Modal>
                </Form>
            )}
        </Formik>
    );
};

export default JobExperienceEditModal;
